import { useEffect, useState } from "react";
import type { Authorization, CounterPrinted, ReportSettings } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { AccountantTab } from "./AccountantTab";
import { ArchiveTab } from "./ArchiveTab";
import { DayTab } from "./DayTab";
import { PlatformsTab } from "./PlatformsTab";
import { ReportDialog } from "./ReportDialog";
import { type ReportPrint } from "./reports";

interface Props {
  /** Who opened Beheer (owner or manager). */
  authorization: Authorization;
}

type Tab = "day" | "archive" | "accountant" | "platforms";

/** An X or Z on screen; `printed`: the server sent it to the printer of the Z-rapport. */
interface Shown {
  print: ReportPrint;
  printed: boolean;
}
const TABS: readonly Tab[] = ["day", "archive", "accountant", "platforms"];

/**
 * Beheer › Rapporten & Z, as in the prototype: Vandaag (the Z page: the day live, the platforms, the
 * drawer, the Z), Z-archief, Boekhouder (the quarter in Excel) and Platformen.
 */
export function ReportsScreen({ authorization }: Props) {
  const { api, report, reportSettingsRevision } = useTill();
  const [tab, setTab] = useState<Tab>("day");
  const [settings, setSettings] = useState<ReportSettings | null>(null);
  const [shown, setShown] = useState<Shown | null>(null);
  /** A Z again on the printer of the Z-rapport (null: the shop has none, the browser prints it). */
  const printZ = (number: number) =>
    api
      .request<{ printed: CounterPrinted }>("POST", `/device/reports/z/${String(number)}/print`, {
        staffToken: authorization.token,
      })
      .then((answer) => answer.printed);

  useEffect(() => {
    let current = true;
    api
      .request<{ settings: ReportSettings }>("GET", "/device/reports/settings", {
        staffToken: authorization.token,
        retry: true,
      })
      .then((answer) => {
        if (current) setSettings(answer.settings);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, authorization.token, report, reportSettingsRevision]);

  return (
    <div className="panel admin-section reports-admin">
      <h2>{t.reports.title}</h2>
      <div className="seg rtabs" role="tablist" aria-label={t.reports.title}>
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            aria-pressed={tab === id}
            onClick={() => {
              setTab(id);
            }}
          >
            {t.reports.tabs[id]}
          </button>
        ))}
      </div>
      {tab === "day" && (
        <DayTab
          authorization={authorization}
          settings={settings}
          onPrint={(print, printed) => {
            setShown({ print, printed });
          }}
          onManagePlatforms={() => {
            setTab("platforms");
          }}
        />
      )}
      {tab === "archive" && (
        <ArchiveTab
          authorization={authorization}
          onOpen={(print) => {
            setShown({ print, printed: false });
          }}
        />
      )}
      {tab === "accountant" && settings && (
        <AccountantTab authorization={authorization} settings={settings} onSaved={setSettings} />
      )}
      {tab === "platforms" && settings && (
        <PlatformsTab authorization={authorization} settings={settings} onSaved={setSettings} />
      )}
      {shown && (
        <ReportDialog
          print={shown.print}
          // a Z can always be printed again (prototype); an X was just printed, or prints from the browser
          onPrint={shown.print.kind === "z" ? () => printZ(shown.print.number ?? 0) : undefined}
          browser={!shown.printed}
          onClose={() => {
            setShown(null);
          }}
        />
      )}
    </div>
  );
}
